import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import "./App.css";
import AdminPanel from "./admin/AdminPanel";
import { ShareWidgetModal } from "./components/ShareWidgetModal";
import { WelcomeTour } from "./components/WelcomeTour";
import LandingPageV2 from "./pages/public/LandingPageV2";
import AuthPage from "./pages/public/AuthPage";
import PrivacyPage from "./pages/public/PrivacyPage";
import TermsPage from "./pages/public/TermsPage";
import ContactPage from "./pages/public/ContactPage";
import ContactDetailsPage from "./pages/public/ContactDetailsPage";
import EmailVerificationPage from "./pages/public/EmailVerificationPage";
import DocsPage from "./pages/public/DocsPage";
import TemplatesShowcasePage from "./pages/public/TemplatesShowcasePage";
import TemplateDetailPage from "./pages/public/TemplateDetailPage";
import Settings from "./pages/cabinet/SettingsPage";
import Documentation from "./pages/cabinet/DocumentationPage";
import ApiDocsPage from "./pages/cabinet/ApiDocsPage";
import AgentsPage from "./pages/cabinet/AgentsPage";
import TestWidgetPage from "./pages/cabinet/TestWidgetPage";
import ProactiveScenariosPage from "./ProactiveScenariosPage";
import { I18nProvider, useI18n, t } from "./i18n";

const API_URL =
  process.env.REACT_APP_API_URL || "https://b.supchats.com";
const WS_URL = process.env.REACT_APP_WS_URL || "wss://b.supchats.com";

axios.defaults.withCredentials = true;

// Глобальный обработчик для ошибок авторизации
const setupAxiosInterceptors = (setToken, navigate) => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url || "";

      // Для админских запросов используем отдельную авторизацию и
      // не трогаем пользовательскую сессию кабинета.
      if (url.includes("/admin/")) {
        return Promise.reject(error);
      }

      if (status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        localStorage.removeItem("sessionOnly");
        // Устанавливаем флаг и редиректим на форму входа
        try { localStorage.setItem("forceAuth", "1"); } catch(e) {}
        navigate("/login");
      }
      return Promise.reject(error);
    }
  );
};

// Обёртка для лендинга: передаёт навигацию на /login
function UnauthenticatedLanding() {
  const navigate = useNavigate();
  const location = useLocation();

  // Редирект со старых URL (?login=1, forceAuth) на /login
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("login") === "1" || localStorage.getItem("forceAuth") === "1") {
      try {
        localStorage.removeItem("forceAuth");
      } catch (e) {}
      navigate("/login", { replace: true });
    }
  }, [location.search, navigate]);

  return (
    <LandingPageV2
      onGetStarted={() => {
        try { localStorage.setItem("auth_mode", "signup"); } catch (e) {}
        navigate("/login?mode=signup");
      }}
      onSignIn={() => {
        try { localStorage.setItem("auth_mode", "login"); } catch (e) {}
        navigate("/login");
      }}
      onBrowseTemplates={() => navigate("/templates")}
      isAuthenticated={false}
    />
  );
}

// AuthenticatedLanding component - лендинг для авторизованных пользователей
function AuthenticatedLanding({ setToken }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("tokenExpiry");
    localStorage.removeItem("sessionOnly");
    sessionStorage.removeItem("token");
    navigate("/");
  };
  
  return (
    <LandingPageV2 
      onGetStarted={() => navigate('/agents')}
      onSignIn={() => navigate('/agents')}
      onLogout={handleLogout}
      isAuthenticated={true}
    />
  );
}

function Dashboard({ token, setToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, setLocale } = useI18n();
  const [canSeeApi, setCanSeeApi] = useState(true);
  
  // Скрытие мобильного меню при смене страницы
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Welcome Tour состояния
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);
  
  // Refs для элементов тура (get_started использует тот же ref, что и agents)
  const agentsRef = useRef(null);
  const tourRefs = {
    agents: agentsRef,
    inbox: useRef(null),
    settings: useRef(null),
    news: useRef(null),
    documentation: useRef(null),
    proactive: useRef(null),
    get_started: agentsRef
  };

  // Шаги welcome tour
  const tourSteps = [
    {
      id: 'welcome',
      title: t('Welcome'),
      description: t("Welcome to Supchats dashboards. Here we're going to tell how to use the service."),
      position: 'bottom'
    },
    {
      id: 'agents',
      title: t('🤖 Agents Tab'),
      description: t('Create and manage your AI assistants here. Each agent can be customized for different purposes and domains.'),
      position: 'bottom'
    },
    {
      id: 'inbox',
      title: t('💬 Inbox Tab'),
      description: t('View and respond to customer conversations. Monitor chat history and manage active conversations.'),
      position: 'bottom'
    },
    {
      id: 'settings',
      title: t('⚙️ Settings Tab'),
      description: t('Manage your account settings, contact details, password and API access.'),
      position: 'bottom'
    },
    {
      id: 'news',
      title: t('📰 News Tab'),
      description: t('Stay updated with the latest features and announcements from Supchats.'),
      position: 'bottom'
    },
    {
      id: 'documentation',
      title: t('📚 Documentation Tab'),
      description: t('Complete guide to using your personal account, creating agents, and installing the chat widget.'),
      position: 'bottom'
    },
    {
      id: 'proactive',
      title: t('🚀 Proactive AI Tab'),
      description: t('Proactively start conversations with visitors based on rules and context. Set up scenarios for new users, returning visitors, or custom attributes.'),
      position: 'bottom'
    },
    {
      id: 'get_started',
      title: t('Ready to get started'),
      description: t('Create your first AI agent to start engaging with your visitors. Click the button below to begin.'),
      position: 'bottom'
    }
  ];

  // Функции для управления welcome tour
  const startWelcomeTour = () => {
    setShowWelcomeTour(true);
    setCurrentTourStep(0);
  };

  const nextTourStep = () => {
    if (currentTourStep < tourSteps.length - 1) {
      setCurrentTourStep(currentTourStep + 1);
    } else {
      finishWelcomeTour();
    }
  };

  const prevTourStep = () => {
    if (currentTourStep > 0) {
      setCurrentTourStep(currentTourStep - 1);
    }
  };

  const finishWelcomeTour = () => {
    setShowWelcomeTour(false);
    setCurrentTourStep(0);
    try {
      localStorage.setItem('welcome_tour_completed', 'true');
      localStorage.setItem('welcome_tour_highlight_add_agent', Date.now().toString());
    } catch (e) {}
    navigate('/agents', { state: { fromTour: true } });
  };

  const skipWelcomeTour = () => {
    finishWelcomeTour();
  };

  // На мобильных при открытом welcome tour — разворачиваем гамбургер, чтобы были видны подсвеченные пункты
  useEffect(() => {
    const isMobile = () => window.innerWidth <= 768;
    const sync = () => {
      if (showWelcomeTour && isMobile()) {
        setMobileMenuOpen(true);
      } else if (!showWelcomeTour) {
        setMobileMenuOpen(false);
      }
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [showWelcomeTour]);

  useEffect(() => {
    const check = async () => {
      try {
        const [limitsRes, apiStatusRes] = await Promise.all([
          axios.get(`${API_URL}/user/limits`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/api/access-status`, {
            headers: { Authorization: `Bearer ${token}` },
            // Для этого запроса куки не нужны, только Bearer-токен.
            // Отключаем credentials, чтобы не было CORS-варнингов.
            withCredentials: false,
          }),
        ]);

        const roleName = (limitsRes.data?.limits?.role_name || limitsRes.data?.limits?.role || "")
          .toString()
          .toLowerCase();
        const viaRole = roleName && roleName !== "free"; // по роли — всем, кроме free

        const apiStatus = apiStatusRes.data || {};
        const viaApproved =
          !!apiStatus.approved_request ||
          (!!apiStatus.has_api_key && !apiStatus.pending_request);

        setCanSeeApi(Boolean(viaRole || viaApproved));
      } catch (e) {
        setCanSeeApi(false);
      }
    };
    check();
  }, [token]);

  // Проверяем, нужно ли показать welcome tour
  useEffect(() => {
    if (token) {
      try {
        const tourCompleted = localStorage.getItem('welcome_tour_completed');
        if (!tourCompleted) {
          // Показываем тур через небольшую задержку после загрузки
          setTimeout(() => {
            startWelcomeTour();
          }, 1000);
        }
      } catch (e) {}
    }
  }, [token]);

  useEffect(() => {
    // Настраиваем перехватчик ошибок
    setupAxiosInterceptors(setToken, navigate);
  }, [setToken, navigate]);

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("tokenExpiry");
    localStorage.removeItem("sessionOnly");
    sessionStorage.removeItem("token");
    navigate("/");
  };

  const handleLocaleToggle = () => {
    const newLocale = locale === 'ru' ? 'en' : 'ru';
    setLocale(newLocale);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard">
      <nav className="nav-bar">
        <div className="nav-dropdown-wrap">
          <button
            type="button"
            className="nav-hamburger"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={t('Menu')}
            aria-expanded={mobileMenuOpen}
          >
            <span className="nav-hamburger-line" />
            <span className="nav-hamburger-line" />
            <span className="nav-hamburger-line" />
          </button>
          <div className={`nav-menu ${mobileMenuOpen ? 'nav-menu--open' : ''}`}>
          <div className="nav-menu-inner">
            <div className="language-switcher">
              <label className="language-toggle-label">
                <input
                  type="checkbox"
                  checked={locale === 'ru'}
                  onChange={handleLocaleToggle}
                  className="language-toggle-checkbox"
                />
                <span className="language-toggle-slider">
                  <span className="language-toggle-text">{locale === 'ru' ? 'RU' : 'EN'}</span>
                </span>
              </label>
            </div>
            <Link 
              ref={tourRefs.inbox}
              to="/inbox" 
              className={`nav-link ${location.pathname.startsWith('/inbox') ? 'active' : ''} ${showWelcomeTour && currentTourStep === 2 ? 'welcome-tour-highlight' : ''}`}
            >
              {t('Inbox')}
            </Link>
            <Link
              ref={agentsRef}
              to="/agents" 
              className={`nav-link ${location.pathname.startsWith('/agents') ? 'active' : ''} ${showWelcomeTour && (currentTourStep === 1 || currentTourStep === 7) ? 'welcome-tour-highlight' : ''}`}
            >
              {t('Agents')}
            </Link>
            <Link
              ref={tourRefs.proactive}
              to="/proactive-scenarios"
              className={`nav-link ${location.pathname.startsWith('/proactive-scenarios') ? 'active' : ''} ${showWelcomeTour && currentTourStep === 6 ? 'welcome-tour-highlight' : ''}`}
            >
              {t('Proactive scenarios')}
            </Link>
            <Link 
              ref={tourRefs.settings}
              to="/settings" 
              className={`nav-link ${location.pathname.startsWith('/settings') ? 'active' : ''} ${showWelcomeTour && currentTourStep === 3 ? 'welcome-tour-highlight' : ''}`}
            >
              {t('Settings')}
            </Link>
            <Link
              ref={tourRefs.news}
              to="/news"
              className={`nav-link ${location.pathname.startsWith('/news') ? 'active' : ''} ${showWelcomeTour && currentTourStep === 4 ? 'welcome-tour-highlight' : ''}`}
            >
              {t('News')}
            </Link>
            <Link
              ref={tourRefs.documentation}
              to="/documentation"
              className={`nav-link ${location.pathname.startsWith('/documentation') ? 'active' : ''} ${showWelcomeTour && currentTourStep === 5 ? 'welcome-tour-highlight' : ''}`}
            >
              {t('Documentation')}
            </Link>
            {canSeeApi && (
              <Link to="/developer" className={`nav-link ${location.pathname.startsWith('/developer') ? 'active' : ''}`}>
                API
              </Link>
            )}
            <button onClick={handleLogout} className="logout-button">
              {t('Logout')}
            </button>
          </div>
        </div>
        </div>
        <div
          className={`nav-overlay ${mobileMenuOpen ? 'nav-overlay--visible' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      </nav>
      <Routes>
        <Route path="/news" element={<News />} />
        <Route path="/inbox" element={<Inbox token={token} setToken={setToken} />} />
        <Route
          path="/agents"
          element={<AgentsPage token={token} setToken={setToken} />}
        />
        <Route
          path="/test-widget/:agentId"
          element={<TestWidgetPage token={token} />}
        />
        <Route
          path="/proactive-scenarios"
          element={<ProactiveScenariosPage />}
        />
        <Route path="/settings" element={<Settings token={token} />} />
        {/* /payment route removed */}
        <Route path="/documentation" element={<Documentation />} />
        {canSeeApi && <Route path="/developer" element={<ApiDocsPage token={token} />} />}
        {!canSeeApi && <Route path="/developer" element={<div style={{ padding: 20 }}>API documentation is available for authorized users.</div>} />}
      </Routes>
      </div>
      
      {/* Welcome Tour */}
      {showWelcomeTour && (
        <WelcomeTour
          steps={tourSteps}
          currentStep={currentTourStep}
          onNext={nextTourStep}
          onPrev={prevTourStep}
          onSkip={skipWelcomeTour}
          onFinish={finishWelcomeTour}
          tourRefs={tourRefs}
        />
      )}
    </div>
  );
}

function News() {
  return (
    <div className="news">
      <div className="news-header">
        <h2>📰 {t('News & Updates')}</h2>
      </div>
      <div className="news-content">
        <div className="news-welcome">
          <div className="welcome-icon">👋</div>
          <h3>{t('Welcome to Supchats!')}</h3>
          <p className="welcome-text">
            {t("Greetings from the Supchats development team! We're excited to have you here.")}
          </p>
          <p className="welcome-text">
            {t('This section will feature updates and announcements about new features, improvements, and enhancements to our AI chat platform. Stay tuned for exciting news!')}
          </p>
          <div className="coming-soon-badge">
            <span>🚀</span>
            <span>{t('More updates coming soon')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Inbox({ token, setToken }) {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [ws, setWs] = useState(null);
  const [agentWs, setAgentWs] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAgentWsConnected, setIsAgentWsConnected] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const adminWsIdRef = useRef(`admin_${Math.random().toString(36).slice(2)}`);
  const [showArchived, setShowArchived] = useState(false);
  const [chatCount, setChatCount] = useState({ active: 0, archived: 0 });
  const [chatStats, setChatStats] = useState({
    active_count: 0,
    archived_count: 0,
    deleted_count: 0,
    total_count: 0,
  });
  const [message, setMessage] = useState("");
  const [aiDisabled, setAiDisabled] = useState(false);
  const { locale } = useI18n();

  // Проверка токена при загрузке компонента
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
  }, [token, navigate]);

  

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(`${API_URL}/agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAgents(response.data);
        if (response.data.length > 0) {
          // Восстанавливаем выбранного агента из localStorage
          let savedId = null;
          try { savedId = parseInt(localStorage.getItem('inbox_selected_agent') || ''); } catch(e) {}
          const found = response.data.find(a => a.id === savedId);
          setSelectedAgent(found || response.data[0]);
          // Сначала пытаемся загрузить активные чаты
          await fetchRecentChats((found || response.data[0]).id, false);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
        if (error.response?.status === 401) {
          navigate("/");
        }
      }
    };

    if (token) {
      fetchAgents();
    }

    // Очистка WebSocket при размонтировании компонента
    return () => {
      if (ws) {
        ws.__manualClose = true;
        ws.close();
        setWs(null);
      }
      if (agentWs) {
        try { agentWs.close(); } catch(e) {}
        setAgentWs(null);
        setIsAgentWsConnected(false);
      }
    };
  }, [token, navigate]);

  // Обновляем функцию fetchRecentChats для работы с новым API
  const fetchRecentChats = async (
    agentId,
    includeArchived = false
  ) => {
    if (!agentId) return;

    try {
      const response = await axios.get(
        `${API_URL}/api/recent-chats/${agentId}?include_archived=${includeArchived}&include_deleted=false`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setChats(response.data.recent_chats || []);
      setChatStats(response.data.stats || {});
    } catch (error) {
      console.error("Failed to fetch recent chats:", error);
      if (error.response?.status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/");
      }
    }
  };

  // Обновляем updateChatList для работы с новым API
  const updateChatList = async () => {
    if (!selectedAgent) return;

    try {
      const response = await fetch(
        `${API_URL}/api/recent-chats/${selectedAgent.id}?include_archived=${showArchived}&include_deleted=false`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();

      if (data.recent_chats) {
        setChats((prevChats) => {
          // Обновляем только те чаты, которые изменились
          const updatedChats = data.recent_chats.map((newChat) => {
            const existingChat = prevChats.find(
              (c) => c.session_id === newChat.session_id
            );
            if (
              existingChat &&
              existingChat.last_message === newChat.last_message &&
              existingChat.last_activity === newChat.last_activity &&
              existingChat.is_active === newChat.is_active &&
              existingChat.is_deleted === newChat.is_deleted
            ) {
              return existingChat;
            }
            return newChat;
          });
          return updatedChats;
        });
      }

      // Обновляем статистику чатов
      if (data.stats) {
        setChatStats(data.stats);
      }
    } catch (error) {
      console.error("Error updating chat list:", error);
    }
  };

  // Первичная загрузка при смене агента/фильтра
  useEffect(() => {
    if (selectedAgent) {
      fetchRecentChats(selectedAgent.id, showArchived);
    }
  }, [selectedAgent, showArchived]);

  // Периодический опрос: всегда включен как резерв, но реже при активном WebSocket
  useEffect(() => {
    if (!selectedAgent) return;
    const periodMs = isConnected ? 30000 : 10000;
    const id = setInterval(() => updateChatList(), periodMs);
    return () => clearInterval(id);
  }, [isConnected, selectedAgent, showArchived]);

  // Очистка WebSocket при смене агента
  useEffect(() => {
    if (ws) {
      ws.__manualClose = true;
      ws.close();
      setWs(null);
    }
    if (agentWs) {
      try { agentWs.close(); } catch(e) {}
      setAgentWs(null);
      setIsAgentWsConnected(false);
    }
    setSelectedChat(null);
    setMessages([]);
  }, [selectedAgent]);

  const fetchChatMessages = async (sessionId) => {
    try {
      setIsLoadingHistory(true);
      const response = await fetch(
        `${API_URL}/api/chat-history/${selectedAgent.id}/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data.messages) {
        processedMessageIds.current.clear();
        data.messages.forEach((msg) => processedMessageIds.current.add(msg.id));
        const normalized = data.messages.map((m) => ({
          ...m,
          is_from_admin: !!m.is_from_admin,
          is_from_bot: !!m.is_from_bot,
          use_support_style: !!m.use_support_style,
          support_name: m.support_name || "Support",
          // хранить timestamp как число (локальное представление для показа)
          timestamp: new Date(m.timestamp).getTime(),
        }));
        setMessages(normalized);
      }
    } catch (error) {
      console.error("Error fetching chat messages:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const connectWebSocket = (agentId, userHash, sessionId) => {
    if (ws) {
      try {
        ws.__manualClose = true;
        ws.close();
      } catch (e) {
        console.error("Error closing existing WebSocket:", e);
      }
      setWs(null);
    }

    // Только для активных чатов подключаем WebSocket
    const selectedChatIsActive = chats.find(
      (chat) => chat.session_id === sessionId
    )?.is_active;

    if (!selectedChatIsActive) {
      setIsConnected(false);
      return; // Не подключаем WebSocket для архивных чатов
    }

    const wsUrl = `${WS_URL}/ws/${agentId}/${userHash}:admin`;

    const socket = new WebSocket(wsUrl);
    socket.__manualClose = false;

    socket.onopen = () => {
      setIsConnected(true);

      // Отправляем запрос на получение последних сообщений
      socket.send(
        JSON.stringify({
          type: "get_messages",
          session_id: sessionId,
        })
      );
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "message") {
        // Дедупликация сообщений
        if (processedMessageIds.current.has(data.message_id)) {
          // Даже если сообщение уже обработано, всё равно обновим список чатов ниже
        } else {
          processedMessageIds.current.add(data.message_id);
        }

        const cleanMessage = data.message?.trim() || "";
        const messageSessionId = data.session_id;

        // Обновляем сообщения ТОЛЬКО если это текущая выбранная сессия
        if (messageSessionId && messageSessionId === sessionId) {
        setMessages((prev) => {
          const messageExists = prev.some((msg) => msg.id === data.message_id);
            if (messageExists) return prev;
          const newMessage = {
            id: data.message_id,
            message: cleanMessage,
              is_from_bot: !!data.is_from_bot,
              is_from_admin: !!data.is_from_admin,
              // локальное время клиента
              timestamp: Date.now(),
              use_support_style: !!data.use_support_style,
            support_name: data.support_name || "Support",
          };
          const updatedMessages = [...prev, newMessage];
            updatedMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          return updatedMessages;
        });
        }

        // Всегда обновляем превью в списке чатов по session_id из сообщения
        if (messageSessionId) {
          setChats((prevChats) => {
            if (!prevChats || prevChats.length === 0) return prevChats;
            const updated = prevChats.map((c) =>
              c.session_id === messageSessionId
                ? { ...c, last_message: cleanMessage, last_activity: data.timestamp || c.last_activity }
                : c
            );
            return [...updated].sort((a, b) => {
              const at = new Date(a.last_activity || 0).getTime();
              const bt = new Date(b.last_activity || 0).getTime();
              return bt - at;
            });
          });
        }
      } else if (data.type === "session_info") {
        // Обновляем информацию о сессии и стилях
        setSelectedChat((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            use_support_style: data.use_support_style || false,
            support_name: data.support_name || "Support",
          };
        });
      } else if (data.type === "error") {
        console.error("WebSocket error message:", data.message);
      } else if (data.type === "pong") {
        // Пропускаем обработку
      } else {
        // Пропускаем обработку
      }
    };

    socket.onclose = (event) => {
      setIsConnected(false);
      setWs(null);

      // Пытаемся переподключиться через 3 секунды только для активных чатов
      if (!socket.__manualClose && selectedChat && selectedChat.is_active && selectedChat.session_id === sessionId) {
      setTimeout(() => {
          if (selectedChat && selectedChat.is_active && selectedChat.session_id === sessionId) {
          connectWebSocket(
            agentId,
            selectedChat.user_hash,
            selectedChat.session_id
          );
        }
      }, 3000);
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      setWs(null);
    };

    setWs(socket);
  };

  // Фоновое подключение админского WS для выбранного агента — получаем события по всем сессиям
  useEffect(() => {
    if (!selectedAgent) return;
    // Закрываем предыдущий, если был
    if (agentWs) {
      try { agentWs.close(); } catch(e) {}
      setAgentWs(null);
      setIsAgentWsConnected(false);
    }

    const url = `${WS_URL}/ws/${selectedAgent.id}/${adminWsIdRef.current}:admin`;
    const socket = new WebSocket(url);

    socket.onopen = () => {
      setIsAgentWsConnected(true);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'message') {
          const sessionId = data.session_id;
          const cleanMessage = (data.message || '').trim();
          // используем локальное время клиента для превью
          const ts = Date.now();
          setChats((prev) => {
            // если чат уже есть — обновим превью/время
            if (prev.some((c) => c.session_id === sessionId)) {
              const updated = prev.map((c) =>
                c.session_id === sessionId
                  ? { ...c, last_message: cleanMessage, last_activity: ts, is_active: c.is_active ?? true }
                  : c
              );
              return [...updated].sort((a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0));
            }
            // если чата нет — добавим минимальную карточку
            const newChat = {
              session_id: sessionId,
              visitor_name: 'Anonymous',
              persistent_id: '',
              last_message: cleanMessage,
              last_activity: ts,
              is_active: true,
            };
            const list = [newChat, ...prev];
            return list.sort((a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0));
          });
        }
      } catch (e) {
        // ignore parsing errors
      }
    };

    socket.onclose = () => {
      setIsAgentWsConnected(false);
    };
    socket.onerror = () => {
      setIsAgentWsConnected(false);
    };

    setAgentWs(socket);
    return () => {
      try { socket.close(); } catch(e) {}
    };
  }, [selectedAgent]);

  const handleChatSelect = async (chat) => {
    if (selectedChat?.session_id === chat.session_id) {
      return;
    }

    // Закрываем текущее соединение
    if (ws) {
      try {
        ws.__manualClose = true;
        ws.close();
      } catch (e) {
        console.error("Error closing WebSocket:", e);
      }
      setWs(null);
    }

    setSelectedChat(chat);
    setMessages([]);
    processedMessageIds.current.clear();
    //asd
    // Устанавливаем состояние ИИ для выбранного чата
    setAiDisabled(chat.disable_ai_response || false);

    // Сначала загружаем историю
    await fetchChatMessages(chat.session_id);

    // Затем подключаемся к WebSocket только для активных чатов
    if (chat.is_active) {
      connectWebSocket(selectedAgent.id, chat.user_hash, chat.session_id);
    } else {
      setIsConnected(false);
    }
  };

  const handleAgentChange = (e) => {
    const agentId = parseInt(e.target.value);
    const agent = agents.find((a) => a.id === agentId);
    setSelectedAgent(agent);
    try { localStorage.setItem('inbox_selected_agent', String(agentId)); } catch(e) {}
    setSelectedChat(null);
    setMessages([]);
    fetchRecentChats(agentId, showArchived);

    // Закрываем WebSocket соединение при смене агента
    if (ws) {
      ws.__manualClose = true;
      ws.close();
      setWs(null);
    }
  };

  const handleSendMessage = async () => {
    if (
      !newMessage.trim() ||
      !selectedChat ||
      !selectedChat.is_active // Проверяем, что чат активный
    )
      return;

    // Проверяем, можно ли отправить сообщение в этот чат (не архивный)
    if (!selectedChat.is_active) {
      alert("Cannot send messages to archived chats");
      return;
    }

    // Если WebSocket не подключен, пытаемся подключиться
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      connectWebSocket(
        selectedAgent.id,
        selectedChat.user_hash,
        selectedChat.session_id
      );

      // Даем время на подключение
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Повторно проверяем подключение
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.error("WebSocket reconnection failed");
        alert("Connection error. Please try again.");
        return;
      }
    }

    const messageToSend = {
      type: "message",
      message: newMessage.trim(),
      user_hash: selectedChat.user_hash,
      session_id: selectedChat.session_id,
      is_from_admin: true,
      use_support_style: selectedChat.use_support_style || false,
      support_name: selectedChat.support_name || "Support",
    };

    try {
      ws.send(JSON.stringify(messageToSend));

      // НЕ добавляем сообщение локально - ждем его через WebSocket
      // Это предотвратит дублирование сообщений
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const formatDate = (value) => {
    const date = new Date(value);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString();
    }
  };

  // Функция для переключения отображения архивных чатов
  const toggleArchivedChats = () => {
    setShowArchived(!showArchived);
  };

  // Добавляем useEffect для автоматической прокрутки к новым сообщениям
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Добавим функцию для изменения статуса чата
  const toggleChatStatus = async (chat) => {
    if (!chat || !selectedAgent) return;

    try {
      const newStatus = !chat.is_active;
      const response = await axios.post(
        `${API_URL}/api/toggle-chat-status/${chat.session_id}`,
        { is_active: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        // Обновляем локальное состояние
        setChats((prevChats) => {
          return prevChats.map((c) => {
            if (c.session_id === chat.session_id) {
              return { ...c, is_active: newStatus };
            }
            return c;
          });
        });

        // Если это текущий выбранный чат, обновляем его статус
        if (selectedChat?.session_id === chat.session_id) {
          setSelectedChat((prev) => ({ ...prev, is_active: newStatus }));

          // Если чат активирован, подключаем WebSocket
          if (newStatus) {
            connectWebSocket(selectedAgent.id, chat.user_hash, chat.session_id);
          } else {
            // Если деактивирован, закрываем соединение
            if (ws) {
              ws.__manualClose = true;
              ws.close();
              setWs(null);
              setIsConnected(false);
            }
          }
        }

        // Обновляем счетчики чатов
        const activeCount = newStatus
          ? chatCount.active + 1
          : chatCount.active - 1;
        const archivedCount = newStatus
          ? chatCount.archived - 1
          : chatCount.archived + 1;
        setChatCount({ active: activeCount, archived: archivedCount });
      }
    } catch (error) {
      console.error("Error toggling chat status:", error);
    }
  };

  // Функция для переключения ответов ИИ
  const toggleAIResponse = async (chat) => {
    if (!chat || !selectedAgent) return;

    try {
      const newAiStatus = !chat.disable_ai_response;
      const response = await axios.post(
        `${API_URL}/api/toggle-ai-response/${chat.session_id}`,
        { disable_ai_response: newAiStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        // Обновляем локальное состояние
        setChats((prevChats) => {
          return prevChats.map((c) => {
            if (c.session_id === chat.session_id) {
              return { ...c, disable_ai_response: newAiStatus };
            }
            return c;
          });
        });

        // Если это текущий выбранный чат, обновляем его статус
        if (selectedChat?.session_id === chat.session_id) {
          setSelectedChat((prev) => ({ ...prev, disable_ai_response: newAiStatus }));
          setAiDisabled(newAiStatus);
        }

        setMessage(`AI responses ${newAiStatus ? 'disabled' : 'enabled'} for this chat`);
      }
    } catch (error) {
      console.error("Error toggling AI response:", error);
      setMessage("Failed to toggle AI response");
    }
  };

  const handleCleanupWelcomeChats = async () => {
    if (!selectedAgent) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/cleanup-welcome-chats`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === "success") {
        setMessage(`Cleanup completed. Deleted ${response.data.deleted_count} welcome chats`);
        fetchRecentChats(selectedAgent.id, showArchived);
      } else {
        setMessage("Failed to clean welcome chats");
      }
    } catch (error) {
      console.error("Error cleaning welcome chats:", error);
      if (error.response?.status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/login");
      } else {
        setMessage("Failed to clean welcome chats");
      }
    }
  };

  return (
    <div className="inbox-container">
      <div className="inbox-header">
        <h2>{t('Inbox')}</h2>
        <div className="inbox-controls">
          {agents.length > 1 && (
            <select
              value={selectedAgent?.id || ""}
              onChange={handleAgentChange}
              className="agent-select"
              title={selectedAgent?.name || t('Select agent')}
            >
              {agents.map((agent) => {
                let statusText = '';
                if (agent.blocked_by_admin === true) {
                  statusText = ` (${t('Suspended by admin')})`;
                } else if (!agent.is_active) {
                  statusText = ` (${t('Inactive')})`;
                } else {
                  statusText = ` (${t('Active')})`;
                }
                
                const displayName = agent.name.length > 20 ? agent.name.substring(0, 20) + '...' : agent.name;
                
                return (
                <option key={agent.id} value={agent.id}>
                    {displayName}{statusText}
                </option>
                );
              })}
            </select>
          )}
          <button
            className={`archive-toggle ${showArchived ? "active" : ""}`}
            onClick={toggleArchivedChats}
          >
            {showArchived ? t('Hide Archived') : t('Show Archived')}
          </button>
          {/* Refresh removed: realtime + periodic polling cover updates */}
          <button
            className="cleanup-button"
            onClick={handleCleanupWelcomeChats}
          >
            {t('Cleanup Welcome Chats')}
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes("Error") || message.includes("Failed") ? "error" : "success"}`}>
          {message}
        </div>
      )}

      <div className="inbox-content messenger-layout">
        <div className="chat-list-container">
          <div className="chat-list-header">
            <h3>
              {t('Recent Chats')}
              <span className="chat-stats">
                {chatStats && (
                  <span>
                    ({t('Active')}: {chatStats.active_count}, {t('Archived')}: {chatStats.archived_count}
                    )
                  </span>
                )}
              </span>
            </h3>
          </div>
          <div className="chat-list">
            {chats.length === 0 ? (
              <div className="no-chats-message">
                {showArchived
                  ? t('No archived chats available')
                  : chatStats.active_count === 0 && chatStats.archived_count > 0
                  ? (
                    <div>
                      <p>{t('No active chats available')}</p>
                      <p>
                        {locale === 'ru'
                          ? `Найдено ${chatStats.archived_count} архивных чатов`
                          : `Found ${chatStats.archived_count} archived chats`}
                      </p>
                      <button 
                        className="show-archived-btn"
                        onClick={() => setShowArchived(true)}
                      >
                        {t('Show Archived Chats')}
                      </button>
                    </div>
                  )
                  : t('No active chats available')}
              </div>
            ) : (
              chats.map((chat) => (
                <div
                  key={chat.session_id}
                  className={`chat-item ${
                    selectedChat?.session_id === chat.session_id
                      ? "selected"
                      : ""
                  } ${!chat.is_active ? "archived" : ""}`}
                  onClick={() => handleChatSelect(chat)}
                >
                  <div className="chat-avatar">
                    {((chat.visitor_name && chat.visitor_name !== "Anonymous") ? 
                      chat.visitor_name : 
                      (chat.persistent_id ? `User: ${chat.persistent_id.slice(-8)}` : "Anonymous")
                    )[0].toUpperCase()}
                  </div>
                  <div className="chat-info">
                    <div className="chat-header">
                      <span className="visitor-name">
                        {(chat.visitor_name && chat.visitor_name !== "Anonymous") ? 
                         chat.visitor_name : 
                         (chat.persistent_id ? `User: ${chat.persistent_id.slice(-8)}` : "Anonymous")}
                        {!chat.is_active && (
                          <span className="archived-label"> ({t('Archived')})</span>
                        )}
                        {chat.is_active && <span className="chat-status active"></span>}
                      </span>
                      <span className="chat-time">{formatDate(chat.last_activity)}</span>
                    </div>
                    <div className="chat-preview">{chat.last_message}</div>
                    {chat.archived_at && (
                      <div className="archive-info">
                        {t('Archived')}: {formatDate(chat.archived_at)}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {selectedChat ? (
          <div className="chat-window">
            <div className="chat-header">
              <div className="chat-title">
                <div className="chat-avatar large">
                  {((selectedChat.visitor_name && selectedChat.visitor_name !== "Anonymous") ? 
                    selectedChat.visitor_name : 
                    (selectedChat.persistent_id ? `User: ${selectedChat.persistent_id.slice(-8)}` : "Anonymous")
                  )[0].toUpperCase()}
                </div>
                <div className="chat-info">
                  <div className="visitor-name">
                    {(selectedChat.visitor_name && selectedChat.visitor_name !== "Anonymous") ? 
                     selectedChat.visitor_name : 
                     (selectedChat.persistent_id ? `User: ${selectedChat.persistent_id.slice(-8)}` : "Anonymous")}
                    {!selectedChat.is_active && (
                      <span className="archived-label"> ({t('Archived')})</span>
                    )}
                  </div>
                  <div className="chat-status-text">
                    {selectedChat.is_active ? t('Online') : t('Archived')}
                  </div>
                </div>
              </div>
              <div className="chat-actions">
                <button
                  className={`status-toggle ${
                    selectedChat.is_active ? "archive" : "activate"
                  }`}
                  onClick={() => toggleChatStatus(selectedChat)}
                >
                  {selectedChat.is_active ? t('Archive Chat') : t('Activate Chat')}
                </button>
                {selectedChat.is_active && (
                  <label className="ai-toggle-label">
                    <input
                      type="checkbox"
                      checked={aiDisabled}
                      onChange={() => toggleAIResponse(selectedChat)}
                      className="ai-toggle-checkbox"
                    />
                    <span>{t('Disable AI responses')}</span>
                  </label>
                )}
              </div>
            </div>
            <div className="messages-container">
              {isLoadingHistory ? (
                <div className="loading-messages">{t('Loading messages...')}</div>
              ) : messages.length === 0 ? (
                <div className="no-messages">{t('No messages yet')}</div>
              ) : (
                messages.map((msg) => {
                  // Определяем класс сообщения на основе всех параметров
                  let messageClass = "user";
                  if (msg.is_from_admin) {
                    messageClass = msg.use_support_style ? "support" : "admin";
                  } else if (msg.is_from_bot) {
                    messageClass = "bot";
                  }

                  return (
                    <div
                      key={`${msg.id}-${msg.timestamp}`}
                      className={`message ${messageClass}`}
                    >
                      <div className="message-content">
                        {Boolean(msg.is_from_admin) && Boolean(msg.use_support_style) && (
                          <div className="support-name">{msg.support_name}</div>
                        )}
                        {msg.message}
                      </div>
                      <div className="message-time">
                        {formatDate(msg.timestamp)}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="message-input">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={
                  selectedChat.is_active
                    ? t('Type a message...')
                    : t('Cannot send messages to archived chats')
                }
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                disabled={!selectedChat.is_active}
                className={!selectedChat.is_active ? "disabled" : ""}
              />
              <button
                onClick={handleSendMessage}
                className={`send-button ${
                  !selectedChat.is_active ? "disabled" : ""
                }`}
                disabled={!selectedChat.is_active}
                title={t('Send message')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="empty-chat-window">
            <div className="empty-chat-message">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <p>{t('Select a chat to start messaging')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function Payment() {
  return (
    <div className="payment">
      <div className="payment-header">
        <h2>💳 {t('Payment & Billing')}</h2>
      </div>
      <div className="payment-content">
        <div className="payment-notice">
          <div className="notice-icon">🚧</div>
          <h3>{t('Automated Payment System Under Development')}</h3>
          <p className="notice-text">
            {t("We're working on implementing automated payment processing for your convenience.")}
            {' '}
            {t('In the meantime, please contact our support team to upgrade your plan or make a payment.')}
          </p>
        </div>
        
        <div className="contact-methods">
          <h4>{t('Contact Support for Payment:')}</h4>
          <div className="contact-grid">
            <div className="contact-item">
              <div className="contact-icon">📧</div>
              <div className="contact-details">
                <div className="contact-label">{t('Email')}</div>
              <div className="contact-value">support@supchats.com</div>
              </div>
            </div>
            
            <div className="contact-item">
              <div className="contact-icon">💬</div>
              <div className="contact-details">
                <div className="contact-label">{t('Telegram')}</div>
                <div className="contact-value">{t('(to be provided)')}</div>
              </div>
            </div>
            
            <div className="contact-item">
              <div className="contact-icon">📱</div>
              <div className="contact-details">
                <div className="contact-label">{t('WhatsApp')}</div>
                <div className="contact-value">{t('(to be provided)')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ContactPage перенесён в pages/ContactPage.js

function App() {
  const [token, setToken] = useState(() => {
    // Проверяем localStorage для "Remember me"
    const rememberMe = localStorage.getItem("rememberMe");
    const tokenExpiry = localStorage.getItem("tokenExpiry");
    
    if (rememberMe === "true" && tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry);
      if (Date.now() < expiryTime) {
        return localStorage.getItem("token");
      } else {
        // Токен истек, очищаем
        localStorage.removeItem("token");
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("tokenExpiry");
      }
    }
    
    // Проверяем сессионную сессию (sessionOnly - токен в localStorage для синхронизации между вкладками)
    const sessionOnly = localStorage.getItem("sessionOnly");
    if (sessionOnly === "true") {
      const sessionToken = localStorage.getItem("token");
      if (sessionToken) {
        // Также сохраняем в sessionStorage для совместимости
        sessionStorage.setItem("token", sessionToken);
        return sessionToken;
      }
    }
    
    // Проверяем sessionStorage для обратной совместимости
    return sessionStorage.getItem("token");
  });
  
  // Синхронизация токена между вкладками и удаление при закрытии браузера
  useEffect(() => {
    // Слушаем изменения в localStorage от других вкладок
    const handleStorageChange = (e) => {
      if (e.key === "token" && e.newValue) {
        const sessionOnly = localStorage.getItem("sessionOnly");
        if (sessionOnly === "true") {
          // Синхронизируем токен между вкладками для сессионных сессий
          sessionStorage.setItem("token", e.newValue);
          setToken(e.newValue);
        }
      }
      if (e.key === "token" && !e.newValue) {
        // Если токен удален в другой вкладке, также удаляем здесь
        const sessionOnly = localStorage.getItem("sessionOnly");
        if (sessionOnly === "true") {
          sessionStorage.removeItem("token");
          setToken(null);
        }
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    
    // Удаляем сессионный токен при закрытии браузера (но не при перезагрузке страницы)
    const handleBeforeUnload = () => {
      const sessionOnly = localStorage.getItem("sessionOnly");
      if (sessionOnly === "true") {
        // Отмечаем что браузер закрывается
        sessionStorage.setItem("closing", "true");
      }
    };
    
    // При загрузке страницы проверяем, был ли браузер закрыт
    const wasClosing = sessionStorage.getItem("closing");
    if (wasClosing === "true") {
      const sessionOnly = localStorage.getItem("sessionOnly");
      if (sessionOnly === "true") {
        // Удаляем токен если браузер был закрыт
        localStorage.removeItem("token");
        localStorage.removeItem("sessionOnly");
        sessionStorage.removeItem("token");
        setToken(null);
      }
      sessionStorage.removeItem("closing");
    }
    
    window.addEventListener("beforeunload", handleBeforeUnload);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Периодическая проверка токена для "Remember me"
  useEffect(() => {
    const checkTokenValidity = () => {
      const rememberMe = localStorage.getItem("rememberMe");
      const tokenExpiry = localStorage.getItem("tokenExpiry");
      
      if (rememberMe === "true" && tokenExpiry) {
        const expiryTime = parseInt(tokenExpiry);
        if (Date.now() >= expiryTime) {
          setToken(null);
          localStorage.removeItem("token");
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("tokenExpiry");
        }
      }
    };

    // Проверяем каждые 5 минут
    const interval = setInterval(checkTokenValidity, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <I18nProvider>
    <Router>
      <Routes>
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/admin/*" element={<AdminPanel />} />
          {/* Public docs/privacy/contact/terms routes */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/contacts" element={<ContactDetailsPage />} />
          <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
          <Route path="/templates" element={<TemplatesShowcasePage />} />
          <Route path="/templates/:templateId" element={<TemplateDetailPage />} />
          {/* Страница входа и регистрации — отдельный роут */}
          <Route
            path="/login"
            element={
              token ? (
                <Navigate to="/agents" replace />
              ) : (
                <AuthPage setToken={setToken} />
              )
            }
          />
          {/* Лендинг V2 */}
          <Route
            path="/v2"
            element={
              token ? (
                <AuthenticatedLanding setToken={setToken} />
              ) : (
                <UnauthenticatedLanding />
              )
            }
          />
        <Route
          path="/*"
          element={
            !token ? (
              <UnauthenticatedLanding />
            ) : (
              <Routes>
                <Route path="/" element={<AuthenticatedLanding setToken={setToken} />} />
                <Route path="/*" element={<Dashboard token={token} setToken={setToken} />} />
              </Routes>
            )
          }
        />
      </Routes>
    </Router>
    </I18nProvider>
  );
}

export default App;

